/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useRef, useState } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Badge, Button, Card, Form as NXUIForm } from '@nexigendigital/nxui'
import { domainAPI, useAddDNSRecordsMutation } from 'api/domain'
import { DNSRecordType } from 'models/enums'
import { DNSRecord } from 'models/domain'
import { replaceNullToEmptyString } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACES
 **********************************************************************************************************/
interface EditingFormValues {
    records: DNSRecord[]
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EditRecordForm() {
    const id = +location.pathname?.split('/')[2]
    const editingRecordFormRef = useRef<FormikProps<EditingFormValues>>(null)
    const {
        data: { records } = { records: [] },
        isError,
        isFetching
    } = domainAPI.endpoints.dnsRecords.useQueryState(id)
    const [, { isLoading }] = useAddDNSRecordsMutation({ fixedCacheKey: 'add-dns-record' })
    const [addDnsRecords, {
        isSuccess: isAddDNSRecordsSuccess,
        isLoading: isEditDNSRecord,
        isError: isAddDNSRecordsError
    }] = useAddDNSRecordsMutation({ fixedCacheKey: 'edit-dns-record' })
    const [mode, setMode] = useState<'view' | 'edit'>('view')
    const column = useMemo(() => {
        const sharedColumns = [
            {
                Header: 'Type',
                accessor: 'type'
            },
            {
                Header: 'Hostname',
                accessor: 'hostname'
            },
            {
                Header: 'Content',
                accessor: 'content'
            }
        ]

        if (isError) {
            return []
        }

        if (records.some((record) => record.priority)) {
            return [
                ...sharedColumns,
                {
                    Header: 'Priority',
                    accessor: 'priority'
                },
                {
                    Header: 'TTL',
                    accessor: 'ttl'
                },
                {
                    Header: '',
                    accessor: 'action',
                    align: 'right'
                }
            ]
        }

        return [
            ...sharedColumns,
            {
                Header: 'TTL',
                accessor: 'ttl'
            },
            {
                Header: '',
                accessor: 'action',
                align: 'right'
            }
        ]
    }, [records])
    const data = useMemo(() => {
        if (isError) {
            return []
        }

        return records.map((record: DNSRecord, index: number) => {
            return {
                type: (
                    <Field
                        name={`records[${index}].type`}
                        type={'select'}
                        list={Object.keys(DNSRecordType).map((key: string) => ({
                            label: key,
                            value: key
                        }))}
                        table={true}
                        component={NXUIForm.TableField}
                    />
                ),
                hostname: (
                    <Field
                        name={`records.${index}.hostname`}
                        type={'text'}
                        component={NXUIForm.TableField}
                        table={true}
                    />
                ),
                content: (
                    record.type === 'SRV'
                        ? (
                            <SRVRecord>
                                <Field
                                    name={`records.${index}.weight`}
                                    placeholder={'Weight'}
                                    type={'text'}
                                    table={true}
                                    component={NXUIForm.TableField}
                                />
                                <Field
                                    name={`records.${index}.port`}
                                    placeholder={'Port'}
                                    type={'text'}
                                    table={true}
                                    component={NXUIForm.TableField}
                                />
                                <Field
                                    name={`records.${index}.destination`}
                                    placeholder={'Destination'}
                                    type={'text'}
                                    table={true}
                                    component={NXUIForm.TableField}
                                />
                            </SRVRecord>
                        )
                        : (
                            <Field
                                name={`records.${index}.content`}
                                type={'text'}
                                table={true}
                                component={NXUIForm.TableField}
                            />
                        )
                ),
                priority: (
                    record.priority
                        ? (
                            <SmallField>
                                <Field
                                    name={`records.${index}.priority`}
                                    type={'text'}
                                    table={true}
                                    component={NXUIForm.TableField}
                                />
                            </SmallField>
                        )
                        : null
                ),
                ttl: (
                    <SmallField>
                        <Field
                            name={`records.${index}.ttl`}
                            type={'text'}
                            table={true}
                            component={NXUIForm.TableField}
                        />
                    </SmallField>
                ),
                action: (
                    <Anchor
                        color={isEditDNSRecord || isFetching ? 'secondary' : 'error'}
                        disabled={isEditDNSRecord || isFetching}
                        onClick={() => {
                            if (editingRecordFormRef.current) {
                                addDnsRecords({
                                    id,
                                    records: editingRecordFormRef.current.values.records.filter((_: DNSRecord, i: number) => i !== index)
                                })
                            }
                        }}
                    >
                        Delete
                    </Anchor>
                )
            }
        })
    }, [records, mode, isEditDNSRecord, isFetching])

    function footer() {
        if (mode === 'view') {
            return (
                <FooterWrapper>
                    <Actions>
                        <Button
                            type={'button'}
                            color={'primary'}
                            onClick={(e: MouseEvent) => {
                                e.preventDefault()
                                setMode('edit')
                            }}
                        >
                            Edit
                        </Button>
                    </Actions>
                </FooterWrapper>
            )
        }

        return (
            <FooterWrapper>
                <Actions>
                    <Button
                        type={'button'}
                        color={'secondary'}
                        onClick={() => setMode('view')}
                    >
                        Back
                    </Button>
                    <Button
                        type={'submit'}
                        color={'primary'}
                        disabled={isLoading || isFetching}
                        form={'editingDNSRecordForm'}
                    >
                        Save
                    </Button>
                </Actions>
            </FooterWrapper>
        )
    }

    useEffect(() => {
        if ((isAddDNSRecordsError || isAddDNSRecordsSuccess) && editingRecordFormRef.current) {
            editingRecordFormRef.current.setSubmitting(false)
            editingRecordFormRef.current.resetForm()
        }
    }, [isAddDNSRecordsError, isAddDNSRecordsSuccess])

    useEffect(() => {
        if (isLoading) {
            setMode('view')
        }
    }, [isLoading])

    if (mode === 'view') {
        return (
            <Card
                table={{
                    render: {
                        columns: () => column,
                        data: () => records.map(({ type, hostname, content, priority, ttl }: DNSRecord) => ({
                            type: <Badge>{type}</Badge>,
                            hostname,
                            content,
                            priority,
                            ttl
                        }))
                    }
                }}
                footer={footer()}
            />
        )
    }

    return (
        <Formik
            innerRef={editingRecordFormRef}
            initialValues={{
                records: records.map((record) => {
                    if (record.type === 'SRV') {
                        const content = record.content.split(' ')

                        return replaceNullToEmptyString({
                            weight: content[0],
                            port: content[1],
                            destination: content[2],
                            ...record
                        })
                    }

                    return replaceNullToEmptyString(record)
                }) as DNSRecord[]
            }}
            enableReinitialize={true}
            onSubmit={({ records }, { setSubmitting }) => {
                addDnsRecords({
                    id,
                    records: records.map((record) => {
                        if (record.type === 'SRV') {
                            return {
                                id: record.id,
                                type: record.type,
                                hostname: record.hostname,
                                content: `${record.weight} ${record.port} ${record.destination}`,
                                priority: record.priority,
                                ttl: record.ttl
                            }
                        }

                        return record
                    })
                })
                setSubmitting(false)
            }}
        >
            {() => (
                <FormWrapper id={'editingDNSRecordForm'}>
                    <Card
                        table={{
                            render: {
                                columns: () => column,
                                data: () => data
                            }
                        }}
                        footer={footer()}
                    />
                </FormWrapper>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const FormWrapper = styled(Form)`
    td:first-child {
        min-width: 140px;
    }
`

const FooterWrapper = styled.div`
    border-top: 1px solid ${props => props.theme['gray--100']};
`

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 12px 12px 12px 0;
`

const SmallField = styled.div`
    max-width: 70px;
`
const SRVRecord = styled.div`
    display: flex;
    justify-content: space-between;

    > div {
        max-width: 70px;
    }
`
